import { useState } from 'react';
import { Text } from '@chakra-ui/react';
import { ModalColumns } from '../../../atoms/modal';
import placeholderExample from '../../../../assets/images/sample-biometric-residence-permit.jpg';
import UploadActions from '../../../atoms/upload-actions';
import UploadExample from '../../../atoms/upload-example';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import UploadCompleteSection from '../../../molecules/uploadComplete';
import { IDate } from '../../../../types';
import AcknowledgeBox from '../../../molecules/acknowledgeBox';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import { validateDate } from '../../../../utilities/validateDate';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import { RequirementsRemedy } from '../../../atoms/RequirementsRemedy';

export interface IProps {
  complete: boolean;
  hasDocument?: boolean;
  fileNames: string[];
  onContinue: () => void;
  onUpload: (metadata?: any) => void;
  onNoBiometrics: () => void;
  onClose: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const BiometricsInnerModal = (props: IProps) => {
  const { docKey, infoOnly, docStatus } = props;

  const [hasDocument, setHasDocument] = useState(props.hasDocument);
  const [dateOfIssue, setDateOfIssue] = useState(getDateFromString(''));
  const [dateOfIssueError, setDateOfIssueError] = useState('');
  const [validUntil, setValidUntil] = useState(getDateFromString(''));
  const [validUntilError, setValidUntilError] = useState('');

  const toggleAcknowledge = (_key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if (
      (value === true && props.docStatus !== 'ACKNOWLEDGED') ||
      (value === false && props.docStatus === 'ACKNOWLEDGED')
    ) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };
  const checkDate = (date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  };

  const isComplete = (): boolean => {
    return !hasDocument
      ? true
      : Boolean(!dateOfIssueError.length && !validUntilError.length && checkDate(dateOfIssue) && checkDate(validUntil));
  };

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  return (
    <>
      {infoOnly ? (
        <>
          <Text data-selector="info-only-copy">
            You have indicated that you have a prior UK visa. During your visa application you may be required to
            provide the government-issued biometric residence ID card you received as part of your prior UK visa.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection
              fileNames={props.fileNames}
              onUpload={props.onUpload}
              onContinue={props.onContinue}
            />
          ) : (
            <>
              <Text data-selector="copy">
                You have indicated that you have a prior UK visa. If possible, please upload the government-issued
                biometric residence ID card you received as part of your UK visa. Please upload the front and the back
                of the card.
              </Text>

              <RadioOptionsWithConfirm
                key={'haveLetter'}
                value={hasDocument}
                label={'Did you receive a Biometric Residence Permit?'}
                onClick={(question: any) => setHasDocument(question.char === 'Y')}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: hasDocument === true
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: hasDocument === false
                  }
                ]}
              />

              {hasDocument === true && (
                <>
                  <DateInputWithConfirm
                    id={`date-of-issue`}
                    label={`What is the date of issue?`}
                    subText={`This date will be under "place and date of issue" on the card`}
                    value={dateOfIssue}
                    errorText={dateOfIssueError}
                    onChangeDD={(evt: any) => {
                      setDateOfIssue({
                        ...dateOfIssue,
                        DD: evt.target.value
                      });
                    }}
                    onChangeMM={(evt: any) => {
                      setDateOfIssue({
                        ...dateOfIssue,
                        MM: evt.target.value
                      });
                    }}
                    onChangeYY={(evt: any) => {
                      setDateOfIssue({
                        ...dateOfIssue,
                        YYYY: evt.target.value
                      });
                    }}
                    onBlur={() => setDateOfIssueError(validateDate(dateOfIssue))}
                  />

                  <DateInputWithConfirm
                    id={`valid-until-date`}
                    label={`What is the valid until date?`}
                    subText={`This date will be under "valid until" on the card`}
                    value={validUntil}
                    errorText={validUntilError}
                    onChangeDD={(evt: any) => {
                      setValidUntil({
                        ...validUntil,
                        DD: evt.target.value
                      });
                    }}
                    onChangeMM={(evt: any) => {
                      setValidUntil({
                        ...validUntil,
                        MM: evt.target.value
                      });
                    }}
                    onChangeYY={(evt: any) => {
                      setValidUntil({
                        ...validUntil,
                        YYYY: evt.target.value
                      });
                    }}
                    onBlur={() => setValidUntilError(validateDate(validUntil))}
                  />
                </>
              )}

              {hasDocument === false && (
                <RequirementsRemedy isSatisfied>
                  This item will be marked as Done. You may be contacted for more information.
                </RequirementsRemedy>
              )}

              <UploadActions
                complete={true}
                disabled={!isComplete()}
                onContinue={() => {
                  if (hasDocument) {
                    props.onUpload({
                      validUntil: getStringFromDate(validUntil),
                      dateOfIssue: getStringFromDate(dateOfIssue),
                      hasDocument: true
                    });
                  } else {
                    props.onNoBiometrics();
                  }
                }}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

const BiometricsModal: React.FC<IProps> = (props: IProps) => {
  const { docStatus } = props;
  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  return (
    <ModalColumns
      id={'biometrics'}
      title={'Previous Biometric Residence Permit'}
      onClose={handleOnClose}
      supportingColumn={<UploadExample altText="Biometric residence permit example" url={placeholderExample} />}
    >
      <BiometricsInnerModal {...props} />
    </ModalColumns>
  );
};

export default BiometricsModal;
